import { Field, FieldArray, useFormikContext, useField } from "formik"
import DropdownMenu from "~/ui-rtk/components/ui/controls/DropdownMenu/DropdownMenu"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import Button from "~/ui-rtk/components/ui/controls/Button"
import ComboboxInput from "~/ui-rtk/components/ui/controls/ComboboxInput"
import { ModelType } from "~/ui-rtk/api/types/custom-attribution.types"
import { AdChannel } from "~/ui-rtk/api/types/custom-attribution-enums"
import React from "react"
import {
  AttributionSource,
  GoogleAnalyticsMetric,
  GoogleAdsMetric,
  TiktokMetric,
  PinterestMetric,
  MetaMetric,
  MarathonMetric,
  ShopifyMetric,
  TripleWhaleMetric,
  getMetricLabel,
  getSourceLabel,
  TRIPLE_WHALE_SOURCE_META,
  TRIPLE_WHALE_SOURCE_TIKTOK,
  TRIPLE_WHALE_SOURCE_PINTEREST,
  TRIPLE_WHALE_SOURCE_GOOGLE_ADS,
  TRIPLE_WHALE_SOURCE_YOUTUBE_ADS,
} from "../constants/attribution"

interface WeightFormValues {
  adChannel: AdChannel | null
  weights: Array<{
    id?: string
    modelType: ModelType
    source: string
    metric: string
    weight: number
  }>
  deletedWeightIds: string[]
}

interface WeightFormRowProps {
  index: number
}

// Custom Dropdown component that adapts DropdownMenu to match Select API
interface CustomDropdownProps {
  options: Array<{ label: string; value: string }>
  value?: string
  disabled?: boolean
  className?: string
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void
  placeholder?: string
  name?: string
}

function CustomDropdown({
  options,
  value,
  disabled,
  className,
  onChange,
  placeholder = "Select...",
  name,
}: CustomDropdownProps) {
  const selectedOption = options.find(opt => opt.value === value)

  const handleItemClick = (selectedValue: string | number) => {
    // Create a synthetic event that matches what Formik expects
    const syntheticEvent = {
      target: {
        name: name || "",
        value: selectedValue,
      },
    } as React.ChangeEvent<HTMLSelectElement>

    onChange?.(syntheticEvent)
  }

  return (
    <DropdownMenu
      options={options}
      onItemClick={handleItemClick}
      disabled={disabled}
      containerClassName="w-full h-system-xl"
      className={`w-full h-system-xl px-system-xs rounded-system-md bg-system-blue-700 border-1 border-system-blue-600 hover:border-system-blue-500 focus:border-system-cyan-400 transition-all duration-200 text-white text-base justify-between shadow-md ${className}`}
    >
      <div className="flex items-center justify-between w-full">
        <span className="text-left flex-1 truncate text-sm">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDownIcon className="w-4 h-4 ml-2 flex-shrink-0 text-system-gray-400" />
      </div>
    </DropdownMenu>
  )
}

// Weight percentage combobox component using Headless UI
interface WeightPercentageComboboxProps {
  name: string
  isError?: boolean
  className?: string
}

// Predefined weight percentage options
const WEIGHT_OPTIONS = [
  { id: 1, label: "25%", value: 25 },
  { id: 2, label: "33%", value: 33 },
  { id: 3, label: "50%", value: 50 },
  { id: 4, label: "75%", value: 75 },
  { id: 5, label: "100%", value: 100 },
  { id: 6, label: "200%", value: 200 },
  { id: 7, label: "300%", value: 300 },
  { id: 8, label: "400%", value: 400 },
]

function WeightPercentageCombobox({
  name,
  isError,
  className,
}: WeightPercentageComboboxProps) {
  const [field, , helpers] = useField(name)

  const handleChange = (value: string) => {
    const numericValue = parseFloat(value.replace("%", ""))
    if (!isNaN(numericValue) && numericValue >= 0) {
      void helpers.setValue(numericValue / 100)
    } else if (value === "") {
      void helpers.setValue("")
    }
  }

  const currentValue = field.value ? `${(field.value * 100).toString()}%` : ""

  return (
    <ComboboxInput
      value={currentValue}
      onChange={handleChange}
      options={WEIGHT_OPTIONS.map(option => ({
        id: option.id,
        label: `${option.value}%`,
        value: `${option.value}%`,
      }))}
      placeholder="0%"
      className={className}
      isError={isError}
    />
  )
}

export function WeightFormRow({ index }: WeightFormRowProps) {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<WeightFormValues>()
  const weight = values.weights[index]
  if (!weight) return null
  const weightErrors = errors.weights?.[index] as {
    source?: string
    metric?: string
    weight?: string
  }
  const weightTouched = touched.weights?.[index] as {
    source?: boolean
    metric?: boolean
    weight?: boolean
  }

  const handleSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSource = e.target.value
    void setFieldValue(`weights.${index}.source`, newSource)
    void setFieldValue(`weights.${index}.metric`, "") // Reset metric when source changes
  }

  const handleMetricChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMetric = e.target.value
    void setFieldValue(`weights.${index}.metric`, newMetric)
  }

  const getAvailableSources = (): string[] => {
    // For Brand Value model type, only allow Marathon source
    if (weight.modelType === ModelType.BRAND_VALUE) {
      return [AttributionSource.MARATHON]
    }

    // For Direct Response model type, use the existing logic
    const baseSources = [
      AttributionSource.GOOGLE_ANALYTICS,
      AttributionSource.GOOGLE_ADS,
      AttributionSource.MARATHON,
      AttributionSource.SHOPIFY,
    ]

    if (values.adChannel === AdChannel.META) {
      return [...baseSources, AttributionSource.META, TRIPLE_WHALE_SOURCE_META]
    }
    if (values.adChannel === AdChannel.TIKTOK) {
      return [
        ...baseSources,
        AttributionSource.TIKTOK,
        TRIPLE_WHALE_SOURCE_TIKTOK,
      ]
    }
    if (values.adChannel === AdChannel.PINTEREST) {
      return [
        ...baseSources,
        AttributionSource.PINTEREST,
        TRIPLE_WHALE_SOURCE_PINTEREST,
      ]
    }
    if (values.adChannel === AdChannel.GOOGLE_ADS) {
      return [
        ...baseSources,
        AttributionSource.GOOGLE_ADS,
        TRIPLE_WHALE_SOURCE_GOOGLE_ADS,
      ]
    }
    if (values.adChannel === AdChannel.YOUTUBE) {
      return [
        ...baseSources,
        AttributionSource.YOUTUBE_ADS,
        TRIPLE_WHALE_SOURCE_YOUTUBE_ADS,
      ]
    }

    return baseSources
  }

  const getAvailableMetrics = (source: string) => {
    if (!source) return []

    // Get all used metrics for this source (same source = same channel attribution)
    const usedMetrics = values.weights
      .filter(
        (w: { source: string; id?: string }) =>
          w.source === source && w.id !== weight.id,
      )
      .map((w: { metric: string }) => w.metric)

    let availableMetrics: Array<{ label: string; value: string }> = []

    // Triple Whale synthetic sources: show only Triple Whale metrics
    if (
      source === TRIPLE_WHALE_SOURCE_META ||
      source === TRIPLE_WHALE_SOURCE_TIKTOK ||
      source === TRIPLE_WHALE_SOURCE_PINTEREST ||
      source === TRIPLE_WHALE_SOURCE_GOOGLE_ADS ||
      source === TRIPLE_WHALE_SOURCE_YOUTUBE_ADS
    ) {
      return Object.values(TripleWhaleMetric)
        .filter(metric => !usedMetrics.includes(metric))
        .map(metric => ({
          label: getMetricLabel("TRIPLE_WHALE", metric),
          value: metric,
        }))
    }

    switch (source as AttributionSource) {
      case AttributionSource.GOOGLE_ANALYTICS:
        availableMetrics = Object.values(GoogleAnalyticsMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.GOOGLE_ADS:
        availableMetrics = Object.values(GoogleAdsMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.TIKTOK:
        availableMetrics = Object.values(TiktokMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.PINTEREST:
        availableMetrics = Object.values(PinterestMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.META:
        availableMetrics = Object.values(MetaMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.MARATHON:
        availableMetrics = Object.values(MarathonMetric)
          .filter(metric => {
            // Don't show metrics that are already used
            if (usedMetrics.includes(metric)) return false

            // For Brand Value model type, only show brand value metrics
            if (weight.modelType === ModelType.BRAND_VALUE) {
              return metric.startsWith("BRAND_VALUE_")
            }

            // For Direct Response model type, only show revenue from search
            if (weight.modelType === ModelType.DIRECT_RESPONSE) {
              return metric === MarathonMetric.REVENUE_FROM_SEARCH
            }

            return true
          })
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.SHOPIFY:
        availableMetrics = Object.values(ShopifyMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel(source, metric),
            value: metric,
          }))
        break
      case AttributionSource.YOUTUBE_ADS:
        availableMetrics = Object.values(TripleWhaleMetric)
          .filter(metric => !usedMetrics.includes(metric))
          .map(metric => ({
            label: getMetricLabel("TRIPLE_WHALE", metric),
            value: metric,
          }))
        break
    }

    return availableMetrics
  }

  return (
    <tr
      className={
        index % 2 === 0 ? "bg-system-blue-600/5" : "bg-system-blue-600/10"
      }
    >
      <td className="px-system-sm py-system-xs align-top">
        <div className="space-y-1">
          <Field name={`weights.${index}.source`}>
            {({ field }: { field: { name: string; value: string } }) => (
              <CustomDropdown
                name={field.name}
                value={field.value}
                onChange={handleSourceChange}
                options={[
                  ...getAvailableSources().map(source => ({
                    label: getSourceLabel(source),
                    value: source,
                  })),
                ]}
                placeholder="Select source..."
                className={`w-full ${
                  weightErrors?.source && weightTouched?.source
                    ? "border-red-500"
                    : ""
                }`}
              />
            )}
          </Field>
          {weightErrors?.source && weightTouched?.source && (
            <p className="text-red-400 text-sm">{weightErrors.source}</p>
          )}
        </div>
      </td>
      <td className="px-system-sm py-system-xs align-top">
        <div className="space-y-1">
          <Field name={`weights.${index}.metric`}>
            {({ field }: { field: { name: string; value: string } }) => (
              <CustomDropdown
                name={field.name}
                value={field.value}
                onChange={handleMetricChange}
                options={[...getAvailableMetrics(weight.source)]}
                disabled={!weight.source}
                placeholder="Select metric..."
                className={`w-full ${
                  weightErrors?.metric && weightTouched?.metric
                    ? "border-red-500"
                    : ""
                }`}
              />
            )}
          </Field>
          {weightErrors?.metric && weightTouched?.metric && (
            <p className="text-red-400 text-sm">{weightErrors.metric}</p>
          )}
        </div>
      </td>
      <td className="px-system-sm py-system-xs align-top">
        <div className="space-y-1 relative">
          <WeightPercentageCombobox
            name={`weights.${index}.weight`}
            isError={!!(weightErrors?.weight && weightTouched?.weight)}
            className={
              weightErrors?.weight && weightTouched?.weight
                ? "!border-system-error-400"
                : ""
            }
          />
          {weightErrors?.weight && weightTouched?.weight && (
            <p className="text-red-400 text-sm">{weightErrors.weight}</p>
          )}
        </div>
      </td>
      <td className="px-system-sm py-system-xs align-top">
        <FieldArray name="weights">
          {({ remove }) => (
            <Button
              variant={{ variant: "text" }}
              size="sm"
              onClick={() => {
                if (weight.id) {
                  void setFieldValue("deletedWeightIds", [
                    ...values.deletedWeightIds,
                    weight.id,
                  ])
                }
                remove(index)
              }}
              type="button"
              className="text-system-error-400 hover:text-system-error-300 hover:bg-system-error-500/10 px-system-xs py-system-3xs rounded-md transition-colors text-sm"
            >
              Remove
            </Button>
          )}
        </FieldArray>
      </td>
    </tr>
  )
}
